const DB_NAME = 'adventures-relief-queue'
const STORE_NAME = 'pending_uploads'
const DB_VERSION = 1

import { authFetch } from '@/shared/lib/auth-fetch'
import { generateVideoThumbnailBlob } from '@/features/photos/lib/video-thumbnail-generator'
import {
  uploadHomeVideoMultipartChunks,
  uploadBaseVideoMultipartChunks,
  shouldUseVideoMultipartUpload,
} from '@/features/photos/lib/upload-video-multipart'
import { isErrorWithStatus } from '@/shared/lib/upload-transient-retry'
import { validateMediaFiles, computeFileContentHash } from '@/features/photos/lib/media-file-validation'






export interface PendingUpload {



  id?: number
  fileBlob: Blob
  type?: 'home' | 'base'
  metadata: {
    homeId?: number
    homeownerId?: number
    homeownerName?: string
    workOrderId?: number
    regionId?: number
    componentName?: string
    description?: string
    tags?: string[]
    filename: string
    contentType: string
    existingPhotoId?: number
    existingPhotoUrl?: string
  }
  status: 'pending' | 'uploading' | 'failed' | 'duplicate'
  attempts: number


}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('IndexedDB is only available in the browser'))
      return
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true })
      }
    }
  })
}

export interface QueueStatus {
  isProcessing: boolean
  totalCount: number
  pendingCount: number
  uploadingCount: number
  failedCount: number
  duplicateCount: number
  activeUpload?: {
    id: number
    filename: string
    progress: number
  }
  items: PendingUpload[]
}

export type QueueListener = (status: QueueStatus) => void

class UploadQueueManager {
  private listeners: Set<QueueListener> = new Set()
  private isProcessing: boolean = false
  private activeUpload?: QueueStatus['activeUpload']
  private wakeLock: any = null

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        console.log('[Queue] Device came online. Resuming queue processing.')
        this.processQueue()
      })
      // Start processing existing items in the queue on startup after a small delay
      setTimeout(() => this.processQueue(), 2000)
    }
  }

  subscribe(listener: QueueListener): () => void {
    this.listeners.add(listener)
    this.notifyListener(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private notifyAll() {
    this.getStatus().then(status => {
      this.listeners.forEach(listener => listener(status))
    })
  }

  private notifyListener(listener: QueueListener) {
    this.getStatus().then(status => listener(status))
  }

  async getStatus(): Promise<QueueStatus> {
    try {
      const items = await this.getAllItems()
      const pending = items.filter(i => i.status === 'pending')
      const uploading = items.filter(i => i.status === 'uploading')
      const failed = items.filter(i => i.status === 'failed')
      const duplicate = items.filter(i => i.status === 'duplicate')

      return {
        isProcessing: this.isProcessing,
        totalCount: items.length,
        pendingCount: pending.length,
        uploadingCount: uploading.length,
        failedCount: failed.length,
        duplicateCount: duplicate.length,
        activeUpload: this.activeUpload,
        items,
      }
    } catch (err) {

      console.error('[Queue] Failed to compute status:', err)
      return {
        isProcessing: false,
        totalCount: 0,
        pendingCount: 0,
        uploadingCount: 0,
        failedCount: 0,
        duplicateCount: 0,
        items: [],
      }

    }
  }

  private async clearDuplicateItems() {
    try {
      const items = await this.getAllItems()
      for (const item of items) {
        if (item.status === 'duplicate' && item.id) {
          await this.deleteItem(item.id)
        }
      }
    } catch (e) {
      console.warn('[Queue] Failed to clear previous duplicate items:', e)
    }
  }

  async enqueuePhotos(
    homeId: number,
    files: File[],
    workOrderId?: number,
    tags?: string[],
    description?: string,
    homeownerId?: number,
    homeownerName?: string
  ): Promise<number[]> {
    const validation = validateMediaFiles(files)
    if (!validation.valid) {
      throw new Error(validation.error || 'Invalid media file format')
    }

    await this.clearDuplicateItems()


    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const ids: number[] = []

    for (const file of files) {
      const item: PendingUpload = {
        fileBlob: file,
        type: 'home',
        metadata: {
          homeId,
          homeownerId,
          homeownerName,
          workOrderId,
          description,
          tags,
          filename: file.name,
          contentType: file.type || 'image/jpeg',
        },

        status: 'pending',
        attempts: 0,
      }
      const request = store.add(item)
      request.onsuccess = (e: any) => {
        ids.push(e.target.result)
      }
    }

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })

    console.log(`[Queue] Enqueued ${files.length} photos with IDs: ${ids.join(', ')}`)
    this.notifyAll()
    this.processQueue()
    return ids
  }

  async enqueueBasePhotos(
    regionId: number,
    files: File[],
    componentName?: string,
    description?: string
  ): Promise<number[]> {
    const validation = validateMediaFiles(files)
    if (!validation.valid) {
      throw new Error(validation.error || 'Invalid media file format')
    }

    await this.clearDuplicateItems()


    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const ids: number[] = []


    for (const file of files) {
      const item: PendingUpload = {
        fileBlob: file,
        type: 'base',
        metadata: {
          regionId,
          componentName,
          description,
          filename: file.name,
          contentType: file.type || 'image/jpeg',
        },
        status: 'pending',
        attempts: 0,
      }
      const request = store.add(item)
      request.onsuccess = (e: any) => {
        ids.push(e.target.result)
      }
    }

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })

    console.log(`[Queue] Enqueued ${files.length} base photos with IDs: ${ids.join(', ')}`)
    this.notifyAll()
    this.processQueue()
    return ids
  }

  async clearQueue(): Promise<void> {
    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    await new Promise<void>((resolve, reject) => {
      const request = store.clear()
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
    this.activeUpload = undefined
    this.isProcessing = false
    await this.releaseWakeLock()
    this.notifyAll()
    console.log('[Queue] Queue cleared successfully.')
  }

  async removeItem(id: number): Promise<void> {
    await this.deleteItem(id)
    if (this.activeUpload && this.activeUpload.id === id) {
      this.activeUpload = undefined
    }
    this.notifyAll()
    console.log(`[Queue] Removed item ${id} from queue.`)
  }

  async retryFailedItems(): Promise<void> {
    const items = await this.getAllItems()
    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    
    for (const item of items) {
      if (item.status === 'failed') {
        item.status = 'pending'
        item.attempts = 0
        store.put(item)
      }
    }
    
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
    
    console.log('[Queue] Retrying failed queue items.')
    this.notifyAll()
    this.processQueue()
  }

  async processQueue() {
    if (this.isProcessing) return
    if (typeof window === 'undefined') return
    if (!navigator.onLine) {
      console.log('[Queue] Device is offline. Postponing queue processing.')
      return
    }

    const items = await this.getAllItems()
    // Find next item to upload (pending or failed but retry-able)
    const nextItem = items.find(
      i => i.status === 'pending' || (i.status === 'failed' && i.attempts < 5)
    )

    if (!nextItem) {
      this.isProcessing = false
      this.activeUpload = undefined
      await this.releaseWakeLock()
      this.notifyAll()
      return
    }

    this.isProcessing = true
    await this.acquireWakeLock()

    try {
      const registeredPhoto: any = await this.uploadItem(nextItem)
      const isDuplicate = Boolean(registeredPhoto?.isDuplicate)

      if (isDuplicate) {
        console.log(`[Queue] Item ${nextItem.id} is duplicate; keeping in queue UI with status 'duplicate'.`)
        nextItem.status = 'duplicate'
        if (registeredPhoto?.id) {
          nextItem.metadata.existingPhotoId = registeredPhoto.id
        }
        if (registeredPhoto?.imageUrl) {
          nextItem.metadata.existingPhotoUrl = registeredPhoto.imageUrl
        }
        const returnedHomeownerId = registeredPhoto?.homeownerId || registeredPhoto?.home?.homeownerId || registeredPhoto?.home?.homeowner?.id
        if (returnedHomeownerId) {
          nextItem.metadata.homeownerId = returnedHomeownerId
        }
        const returnedHomeownerName = registeredPhoto?.home?.homeowner?.fullname || registeredPhoto?.homeowner?.fullname || registeredPhoto?.homeOwner?.fullname || registeredPhoto?.home?.name
        if (returnedHomeownerName) {
          nextItem.metadata.homeownerName = returnedHomeownerName
        }
        await this.updateItem(nextItem)

      } else {
        await this.deleteItem(nextItem.id!)
      }


      // Dispatch custom window event
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('photo-uploaded', {
          detail: { 
            homeId: nextItem.metadata.homeId, 
            regionId: nextItem.metadata.regionId, 
            componentName: nextItem.metadata.componentName, 
            id: nextItem.id,
            type: nextItem.type || 'home',
            isDuplicate,
            photo: registeredPhoto
          }
        }))
      }

      console.log(`[Queue] Successfully processed item ${nextItem.id} (isDuplicate: ${isDuplicate})`)
    } catch (error: any) {
      const msg = error?.message || String(error)
      if (msg.includes('already been uploaded')) {
        console.warn(`[Queue] Item ${nextItem.id} skipped duplicate:`, msg)
        nextItem.status = 'duplicate'
        await this.updateItem(nextItem)
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('photo-uploaded', {
            detail: {
              homeId: nextItem.metadata.homeId,
              regionId: nextItem.metadata.regionId,
              componentName: nextItem.metadata.componentName,
              id: nextItem.id,
              type: nextItem.type || 'home',
              isDuplicate: true,
            }
          }))
        }
      }

 else {
        console.error(`[Queue] Failed uploading item ${nextItem.id}:`, error)
        nextItem.status = 'failed'
        const status = isErrorWithStatus(error) ? error.status : undefined
        const is4xx = typeof status === 'number' && status >= 400 && status < 500
        if (is4xx) {
          nextItem.attempts = 5 // Max attempts reached: do not retry 4xx client errors
        } else {
          nextItem.attempts += 1
        }

        await this.updateItem(nextItem)
      }
    }



    this.activeUpload = undefined
    this.isProcessing = false
    this.notifyAll()

    // Process next item after a short delay
    setTimeout(() => this.processQueue(), 1000)
  }

  private async uploadItem(item: PendingUpload): Promise<any> {
    item.status = 'uploading'
    await this.updateItem(item)
    this.activeUpload = {
      id: item.id!,
      filename: item.metadata.filename,
      progress: 0,
    }
    this.notifyAll()

    const isBasePhoto = item.type === 'base'
    const file = item.fileBlob instanceof File
      ? item.fileBlob
      : new File([item.fileBlob], item.metadata.filename, { type: item.metadata.contentType })

    const isVideo = item.metadata.contentType.startsWith('video/') || /\.(mp4|mov|webm|m4v)$/i.test(item.metadata.filename)
    let thumbnailBlob: Blob | null = null

    if (isVideo) {
      thumbnailBlob = await generateVideoThumbnailBlob(item.fileBlob).catch(() => null)
    }

    const contentHash = await computeFileContentHash(item.fileBlob).catch(() => undefined)

    if (shouldUseVideoMultipartUpload(file)) {
      if (isBasePhoto) {
        return uploadBaseVideoMultipartChunks(
          item.metadata.regionId!,
          file,
          {
            description: item.metadata.description,
            componentName: item.metadata.componentName,
            thumbnailBlob,
            contentHash,
          },
          (progress) => {
            if (this.activeUpload && this.activeUpload.id === item.id) {
              const pct = progress.fileSize > 0 ? Math.round((progress.uploadedBytes / progress.fileSize) * 100) : 0
              this.activeUpload.progress = pct
              this.notifyAll()
            }
          }
        )
      } else {
        return uploadHomeVideoMultipartChunks(
          item.metadata.homeId!,
          file,
          {
            workOrderId: item.metadata.workOrderId,
            description: item.metadata.description,
            tags: item.metadata.tags,
            thumbnailBlob,
            contentHash,
          },
          (progress) => {
            if (this.activeUpload && this.activeUpload.id === item.id) {
              const pct = progress.fileSize > 0 ? Math.round((progress.uploadedBytes / progress.fileSize) * 100) : 0
              this.activeUpload.progress = pct
              this.notifyAll()
            }
          }
        )
      }
    }




    if (isBasePhoto) {
      // 1. Get base presigned upload URL and S3 Key from the backend
      const presignRes = await authFetch('/photos/base/presign-upload', {
        method: 'POST',
        body: JSON.stringify({
          regionId: item.metadata.regionId,
          filename: item.metadata.filename,
          contentType: item.metadata.contentType,
          ...(thumbnailBlob ? { includeThumbnail: true } : {}),
        }),
      })
      if (!presignRes.ok) {
        const json = await presignRes.json().catch(() => ({}))
        const msg = json?.message || `Base presigned URL generation failed: status ${presignRes.status}`
        throw new Error(msg)
      }
      const presignData = await presignRes.json()
      if (presignData.data?.isDuplicate && presignData.data?.photo) {
        console.log('[Queue] Pre-flight base duplicate check matched; skipping single upload.')
        return { ...presignData.data.photo, isDuplicate: true }
      }
      const { uploadUrl, key, thumbnailUploadUrl, thumbnailKey } = presignData.data


      // 2. Direct PUT raw binary file upload directly to R2 using XMLHttpRequest
      const uploadPromises: Promise<void>[] = [
        this.uploadToR2(uploadUrl, item.fileBlob, item.metadata.contentType, (progress) => {
          if (this.activeUpload && this.activeUpload.id === item.id) {
            this.activeUpload.progress = progress
            this.notifyAll()
          }
        })
      ]

      if (thumbnailBlob && thumbnailUploadUrl) {
        uploadPromises.push(
          this.uploadToR2(thumbnailUploadUrl, thumbnailBlob, 'image/jpeg', () => {})
        )
      }

      await Promise.all(uploadPromises)

      // 3. Register the uploaded base photo details in NestJS DB
      const registerRes = await authFetch('/photos/base/register', {
        method: 'POST',
        body: JSON.stringify({
          regionId: item.metadata.regionId,
          description: item.metadata.description,
          componentName: item.metadata.componentName,
          key,
          ...(thumbnailBlob && thumbnailKey ? { thumbnailKey } : {}),
          filename: item.metadata.filename,
          contentType: item.metadata.contentType,
          fileSize: item.fileBlob.size,
        }),
      })
      if (!registerRes.ok) {
        const json = await registerRes.json().catch(() => ({}))
        const msg = json?.message || `Base registration failed: status ${registerRes.status}`
        throw new Error(msg)
      }
      const registerData = await registerRes.json().catch(() => ({}))
      return registerData.data
    } else {
      // Standard home photo upload
      const presignRes = await authFetch('/photos/presign-upload', {
        method: 'POST',
        body: JSON.stringify({
          homeId: item.metadata.homeId,
          filename: item.metadata.filename,
          contentType: item.metadata.contentType,
          ...(thumbnailBlob ? { includeThumbnail: true } : {}),
          ...(contentHash ? { contentHash } : {}),
        }),
      })
      if (!presignRes.ok) {
        const json = await presignRes.json().catch(() => ({}))
        const msg = json?.message || `Presigned URL generation failed: status ${presignRes.status}`
        throw new Error(msg)
      }
      const presignData = await presignRes.json()
      if (presignData.data?.isDuplicate && presignData.data?.photo) {
        console.log('[Queue] Pre-flight duplicate check matched; skipping single upload.')
        return { ...presignData.data.photo, isDuplicate: true }
      }

      const { uploadUrl, key, thumbnailUploadUrl, thumbnailKey } = presignData.data

      // 2. Direct PUT raw binary file upload directly to R2 using XMLHttpRequest
      const uploadPromises: Promise<void>[] = [
        this.uploadToR2(uploadUrl, item.fileBlob, item.metadata.contentType, (progress) => {
          if (this.activeUpload && this.activeUpload.id === item.id) {
            this.activeUpload.progress = progress
            this.notifyAll()
          }
        })
      ]

      if (thumbnailBlob && thumbnailUploadUrl) {
        uploadPromises.push(
          this.uploadToR2(thumbnailUploadUrl, thumbnailBlob, 'image/jpeg', () => {})
        )
      }

      await Promise.all(uploadPromises)

      // 3. Register the uploaded photo details in NestJS DB
      const registerRes = await authFetch('/photos/register', {
        method: 'POST',
        body: JSON.stringify({
          homeId: item.metadata.homeId,
          workOrderId: item.metadata.workOrderId,
          description: item.metadata.description,
          tags: item.metadata.tags,
          key,
          ...(thumbnailBlob && thumbnailKey ? { thumbnailKey } : {}),
          ...(contentHash ? { contentHash } : {}),
          filename: item.metadata.filename,
          contentType: item.metadata.contentType,
          fileSize: item.fileBlob.size,
        }),
      })

      if (!registerRes.ok) {
        const json = await registerRes.json().catch(() => ({}))
        const msg = json?.message || `Registration failed: status ${registerRes.status}`
        throw new Error(msg)
      }
      const registerData = await registerRes.json().catch(() => ({}))
      return registerData.data
    }

  }

  private uploadToR2(url: string, blob: Blob, contentType: string, onProgress: (pct: number) => void): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('PUT', url, true)
      xhr.setRequestHeader('Content-Type', contentType)

      if (xhr.upload) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100)
            onProgress(percent)
          }
        }
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
        } else {
          reject(new Error(`Direct PUT upload failed with status ${xhr.status}`))
        }
      }

      xhr.onerror = () => {
        reject(new Error('Direct PUT upload network error'))
      }

      xhr.send(blob)
    })
  }

  private async getAllItems(): Promise<PendingUpload[]> {
    const db = await openDB()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const store = tx.objectStore(STORE_NAME)
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  private async updateItem(item: PendingUpload): Promise<void> {
    const db = await openDB()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      const request = store.put(item)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  private async deleteItem(id: number): Promise<void> {
    const db = await openDB()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      const request = store.delete(id)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  private async acquireWakeLock() {
    if (typeof window !== 'undefined' && 'wakeLock' in navigator && !this.wakeLock) {
      try {
        this.wakeLock = await (navigator as any).wakeLock.request('screen')
        console.log('[Queue] Wake lock acquired successfully.')
      } catch (err) {
        console.error('[Queue] Failed to acquire wake lock:', err)
      }
    }
  }

  private async releaseWakeLock() {
    if (this.wakeLock) {
      try {
        await this.wakeLock.release()
        console.log('[Queue] Wake lock released.')
      } catch (err) {
        console.error('[Queue] Failed to release wake lock:', err)
      } finally {
        this.wakeLock = null
      }
    }
  }
}

export const uploadQueueManager = new UploadQueueManager()
